/* src/components/applicantFlow/ApplicantRegisterManual.jsx */
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
  reload,
  GoogleAuthProvider,
  signInWithPopup,
} from "firebase/auth";
import { assignUserRole } from "../../../utils/roleManagement"; // ← NEW (already added)
import { auth } from "../../../../firebase";
import { getFunctions, httpsCallable } from "firebase/functions";
import { serverTimestamp } from "firebase/firestore";
import { setUser } from "../../../../redux/actions";
import tpcLogo from "../../../../images/TPC-logo-light.png";
import tpcAndUSLogo from "../../../../images/AndUsLoginLogo.png";
import LoadingCircleSmall from "../../../utils/LoadingCircleSmall";
import "./ApplicantRegister.css";

/*──── CONFIG specific to Applicant flow ────*/
const BASE_CONFIG = {
  company: "TPC_Group",
  collectionBrand: "TechPerformanceCentre",
  systemName: "Applicants",
  successRoute: "/learner-dashboard",
  loginRoute: "/applicant-login-manual",
  defaultState: "Applicant",
  claim: "applicant-TPC_Group-TechPerformanceCentre",
};

export default function ApplicantRegisterManual({
  applicationScope = "",
  learnerFlag = false,
  loginRoute = BASE_CONFIG.loginRoute,
  successRoute = BASE_CONFIG.successRoute,
  registerTitle = "Create Account to Apply",
  welcomeText = "Welcome to TPC",
  passwordPlaceholder = "Enter your password",
}) {
  const CONFIG = { ...BASE_CONFIG, loginRoute, successRoute };
  const VERIFY_LINK_EXPIRY_SECONDS = 3600;
  /* form fields */
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [pw, setPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [terms, setTerms] = useState(false);

  /* ui */
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [verifyStep, setVerify] = useState(false);
  const [googleSignInLoading, setGoogleSignInLoading] = useState(false);
  const [verificationSentAtMs, setVerificationSentAtMs] = useState(0);
  const [verificationTimeLeft, setVerificationTimeLeft] = useState(
    VERIFY_LINK_EXPIRY_SECONDS,
  );
  const [resendBusy, setResendBusy] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const fn = getFunctions();
  const nav = useNavigate();
  const disp = useDispatch();
  const fsOp = httpsCallable(fn, "firestoreOperations");

  const getScopedApplicantFields = () => {
    if (!applicationScope) return {};
    return {
      applicationScope,
      discipline:
        applicationScope === "trade"
          ? "Trade"
          : applicationScope === "ai-professional"
            ? "AI in the workplace"
            : undefined,
    };
  };

  const applyScopedSessionFlags = () => {
    if (applicationScope) {
      sessionStorage.setItem("applicationScope", applicationScope);
    } else {
      sessionStorage.removeItem("applicationScope");
    }
    if (learnerFlag) {
      sessionStorage.setItem("tpcLearner", "true");
    } else {
      sessionStorage.removeItem("tpcLearner");
    }
    sessionStorage.setItem("learnerJustLoggedIn", "1");
  };

  const navigateAfterRegistration = () => {
    applyScopedSessionFlags();
    nav("/learner-welcome-home");
  };

  /*──────────────── register handler ───────────────*/
  const handleRegister = async (e) => {
    e.preventDefault();
    setErr("");
    setMsg("");

    if (!terms) return setErr("Please accept Terms & Privacy.");
    if (!(email && name && pw && confirmPw))
      return setErr("All fields are required.");
    if (pw.length < 6) return setErr("Password must be 6+ characters.");
    if (pw !== confirmPw) return setErr("Passwords do not match.");

    setBusy(true);
    try {
      console.log("[AR] ▶ createUserWithEmailAndPassword");
      const { user } = await createUserWithEmailAndPassword(auth, email, pw);
      console.log("[AR] Email registration user details:", {
        uid: user.uid,
        email: user.email,
        name: name,
      });
      disp(setUser({ uid: user.uid, email: user.email, name }));

      /* ── NEW: assign custom claim ───────────────────────────────── */
      await assignUserRole(user.uid, CONFIG.claim);
      console.log("[AR] custom claim set:", CONFIG.claim);

      /* Optional: force-refresh ID-token so the claim is present */
      await auth.currentUser.getIdToken(true);

      /* stub doc in Applicants — preserve pre-assigned intake/funder fields if present */
      let existingApplicant = null;
      try {
        const existingRes = await fsOp({
          operation: "read",
          company: CONFIG.company,
          collectionBrand: CONFIG.collectionBrand,
          systemName: CONFIG.systemName,
          uniqueId: email,
          auth: true,
        });
        existingApplicant =
          existingRes?.data?.data ||
          (existingRes?.data && typeof existingRes.data === "object"
            ? existingRes.data
            : null);
      } catch (_) {
        existingApplicant = null;
      }

      const isPreAssigned =
        Boolean(existingApplicant?.assignedVia) ||
        Boolean(existingApplicant?.funderId) ||
        Boolean(existingApplicant?.intakeAssignedByEmail) ||
        String(existingApplicant?.feedback || "")
          .toLowerCase()
          .trim() === "learner";

      await fsOp({
        operation: "write",
        company: CONFIG.company,
        collectionBrand: CONFIG.collectionBrand,
        systemName: CONFIG.systemName,
        uniqueId: email,
        documentData: {
          email,
          fullName: name || existingApplicant?.fullName || "",
          ...getScopedApplicantFields(),
          hasRegistered: true,
          registeredAt: new Date().toISOString(),
          authUid: user.uid,
          ...(isPreAssigned
            ? {
                isCompleted: true,
                feedback: "Learner",
                state: "Learner",
                status: "Learner",
              }
            : { isCompleted: false }),
        },
        auth: true,
      });
      console.log("[AR] Applicant stub written with Applicant state");

      /* verification mail */
      await sendEmailVerification(user);
      setVerificationSentAtMs(Date.now());
      setVerificationTimeLeft(VERIFY_LINK_EXPIRY_SECONDS);
      setVerify(true);
      setMsg("Verification link sent! Check your inbox.");
    } catch (e) {
      console.error("[AR] register error:", e);

      // Handle specific Firebase auth errors with user-friendly messages
      if (e.code === "auth/email-already-in-use") {
        setErr(
          "This email is already in use. Please try a different email or log in if you already have an account.",
        );
      } else if (e.code === "auth/weak-password") {
        setErr(
          "Password is too weak. Please choose a stronger password (at least 6 characters).",
        );
      } else if (e.code === "auth/invalid-email") {
        setErr("Please enter a valid email address.");
      } else {
        setErr(e.message || "Registration failed. Please try again.");
      }
    } finally {
      setBusy(false);
    }
  };

  /*──────────────── auto-check email verification ───────────────*/
  useEffect(() => {
    if (!verifyStep || !auth.currentUser) return;

    const checkVerification = async () => {
      try {
        const u = auth.currentUser;
        await reload(u);
        if (u.emailVerified) {
          console.log("[AR] Email verified → redirecting to dashboard");
          navigateAfterRegistration();
        }
      } catch (e) {
        console.error("[AR] Error checking verification:", e);
      }
    };

    // Check immediately
    checkVerification();

    // Check every 2 seconds
    const interval = setInterval(checkVerification, 2000);

    return () => clearInterval(interval);
  }, [verifyStep, nav]);

  useEffect(() => {
    if (!verifyStep || verificationSentAtMs <= 0) return undefined;
    const timer = setInterval(() => {
      const elapsed = Math.floor((Date.now() - verificationSentAtMs) / 1000);
      const left = Math.max(VERIFY_LINK_EXPIRY_SECONDS - elapsed, 0);
      setVerificationTimeLeft(left);
    }, 1000);
    return () => clearInterval(timer);
  }, [verifyStep, verificationSentAtMs]);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timer = setInterval(() => {
      setResendCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const handleResendVerification = async () => {
    if (resendBusy || resendCooldown > 0) return;
    const currentUser = auth.currentUser;
    if (!currentUser) {
      setErr("Session expired. Please register/login again.");
      return;
    }
    setResendBusy(true);
    setErr("");
    setMsg("");
    try {
      await sendEmailVerification(currentUser);
      setVerificationSentAtMs(Date.now());
      setVerificationTimeLeft(VERIFY_LINK_EXPIRY_SECONDS);
      setResendCooldown(60);
      setMsg("New verification link sent. Please check your inbox and spam.");
    } catch (e) {
      setErr(e?.message || "Failed to resend verification link.");
    } finally {
      setResendBusy(false);
    }
  };

  /*──────────────── Google sign-in handler ───────────────*/
  const handleGoogleSignIn = async () => {
    setErr("");
    setMsg("");

    setGoogleSignInLoading(true);
    setBusy(true);

    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      console.log("[AR] Google sign-in successful:", user.email);

      // Extract name from Google account
      const displayName = user.displayName || user.email.split("@")[0];
      console.log("[AR] Google user details:", {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        extractedName: displayName,
      });
      disp(setUser({ uid: user.uid, email: user.email, name: displayName }));

      // Check if user already exists in Applicants
      let userExists = false;
      let existingUserProfile = null;
      try {
        const checkResult = await fsOp({
          operation: "read",
          company: CONFIG.company,
          collectionBrand: CONFIG.collectionBrand,
          systemName: CONFIG.systemName,
          uniqueId: user.email,
          auth: true,
        });
        console.log("[AR] User check result:", checkResult.data);
        userExists = checkResult.data && checkResult.data.name;
        existingUserProfile = checkResult.data;
      } catch (readError) {
        console.log("[AR] User doesn't exist yet, will create new profile");
        userExists = false;
      }

      // Assign custom claim (for both new and existing users)
      await assignUserRole(user.uid, CONFIG.claim);
      console.log("[AR] custom claim set:", CONFIG.claim);

      // Force-refresh ID-token
      await auth.currentUser.getIdToken(true);

      // If user doesn't exist or profile is incomplete, create profile
      if (!userExists) {
        console.log("[AR] Creating Google user profile in Applicants");

        // Create complete profile in Applicants with Google details
        await fsOp({
          operation: "write",
          company: CONFIG.company,
          collectionBrand: CONFIG.collectionBrand,
          systemName: CONFIG.systemName,
          uniqueId: user.email,
          documentData: {
            email: user.email,
            fullName: displayName,
            photoURL: user.photoURL || "",
            uid: user.uid,
            isCompleted: false,
            authProvider: "google",
            emailVerified: true,
            hasRegistered: true,
            registeredAt: new Date().toISOString(),
            lastLoginAt: new Date().toISOString(),
            ...getScopedApplicantFields(),
            // DO NOT include: name, ageBracket, countryCode, currentStep, phone, reason, state, status, subjectUnderstanding, timestamp
          },
          auth: true,
        });
        console.log(
          "[AR] Profile created for Google user with Applicant state",
        );
      } else {
        console.log("[AR] Existing user found, logging in");
        try {
          await fsOp({
            operation: "write",
            company: CONFIG.company,
            collectionBrand: CONFIG.collectionBrand,
            systemName: CONFIG.systemName,
            uniqueId: user.email,
            documentData: {
              email: user.email,
              hasRegistered: true,
              registeredAt:
                existingUserProfile?.registeredAt || new Date().toISOString(),
              lastLoginAt: new Date().toISOString(),
              lastSeenAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            },
            auth: true,
          });
        } catch (trackErr) {
          console.warn("[AR] Unable to track Google login:", trackErr);
        }
      }

      // Google accounts are automatically verified
      // New users go to complete their profile, existing users go to dashboard
      if (!userExists) {
        console.log("[AR] New Google user, redirecting to complete profile");
        navigateAfterRegistration();
      } else {
        console.log("[AR] Existing Google user, checking profile state");
        if (
          existingUserProfile?.state === "Applicant" ||
          !existingUserProfile?.isCompleted
        ) {
          navigateAfterRegistration();
        } else {
          applyScopedSessionFlags();
          nav(CONFIG.successRoute);
        }
      }
    } catch (e) {
      console.error("[AR] Google sign-in error:", e);
      if (e.code === "auth/popup-closed-by-user") {
        setErr("Sign-in cancelled. Please try again.");
      } else if (e.code === "auth/popup-blocked") {
        setErr("Pop-up blocked. Please enable pop-ups and try again.");
      } else {
        setErr(e.message || "Google sign-in failed.");
      }
    } finally {
      setGoogleSignInLoading(false);
      setBusy(false);
    }
  };

  /*──────── render ────────*/
  return (
    <>
      <div
        className="ar-container"
        style={{
          position: "relative",
          filter: googleSignInLoading ? "blur(3px)" : "none",
          transition: "filter 0.3s ease",
        }}
      >
        {/* left banner */}
        <div className="ar-left">
          <div className="ar-logo-box">
            <img src={tpcAndUSLogo} alt="TPC" className="ar-logo" />
          </div>
          <div className="ar-banner-text">{welcomeText}</div>
        </div>

        {/* right pane */}
        <div className="ar-right">
          {verifyStep ? (
            /* verification message */
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "60px 40px",
                textAlign: "center",
                background: "linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)",
                borderRadius: "16px",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
                border: "1px solid #e5e7eb",
              }}
            >
              <div
                style={{
                  width: "80px",
                  height: "80px",
                  borderRadius: "50%",
                  background:
                    "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: "24px",
                  animation: "pulse 2s ease-in-out infinite",
                }}
              >
                <svg
                  width="40"
                  height="40"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <polyline points="22,6 12,13 2,6"></polyline>
                </svg>
              </div>
              <p
                style={{
                  fontSize: "15px",
                  color: "#6b7280",
                  lineHeight: "1.6",
                  margin: "0 0 16px 0",
                  maxWidth: "400px",
                }}
              >
                We've sent a verification link to <strong>{email}</strong>.
                <br />
                Please check your inbox and click the link to verify your email.
                <br />
                <span
                  style={{
                    fontSize: "14px",
                    color: "#1e3a8a",
                    fontWeight: "600",
                    backgroundColor: "#dbeafe",
                    padding: "8px 12px",
                    borderRadius: "6px",
                    display: "inline-block",
                    marginTop: "12px",
                  }}
                >
                  💡 Don't forget to check your spam folder!
                </span>
              </p>
              <p
                style={{
                  margin: "0 0 14px 0",
                  fontSize: "14px",
                  color: verificationTimeLeft <= 300 ? "#b91c1c" : "#1e3a8a",
                  fontWeight: 600,
                }}
              >
                Verification link expires in{" "}
                {`${Math.floor(verificationTimeLeft / 60)
                  .toString()
                  .padStart(2, "0")}:${(verificationTimeLeft % 60)
                  .toString()
                  .padStart(2, "0")}`}
              </p>
              <button
                type="button"
                onClick={handleResendVerification}
                disabled={resendBusy || resendCooldown > 0}
                style={{
                  borderRadius: "999px",
                  border: "1px solid #1e3a8a",
                  background: resendBusy || resendCooldown > 0 ? "#cbd5e1" : "#dbeafe",
                  color: "#1e3a8a",
                  fontWeight: 700,
                  padding: "10px 16px",
                  cursor: resendBusy || resendCooldown > 0 ? "not-allowed" : "pointer",
                  marginBottom: "10px",
                }}
              >
                {resendBusy
                  ? "Sending..."
                  : resendCooldown > 0
                    ? `Resend in ${resendCooldown}s`
                    : "Resend verification email"}
              </button>
              {err && (
                <p
                  style={{
                    fontSize: "14px",
                    color: "#ef4444",
                    marginTop: "16px",
                    padding: "12px",
                    background: "#fef2f2",
                    borderRadius: "8px",
                    border: "1px solid #fecaca",
                  }}
                >
                  {err}
                </p>
              )}
            </div>
          ) : (
            /* main form */
            <form className="ar-form" onSubmit={handleRegister}>
              <h2 className="ar-title">{registerTitle}</h2>
              {err && <p className="ar-err">{err}</p>}
              <input
                type="text"
                placeholder="Full Name"
                className="ar-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
              <input
                type="email"
                placeholder="Email"
                className="ar-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <input
                type="password"
                placeholder={passwordPlaceholder}
                className="ar-input"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                required
              />
              <input
                type="password"
                placeholder="Confirm your password"
                className="ar-input"
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                required
              />
              <div className="ar-check-wrap">
                <input
                  type="checkbox"
                  id="terms-checkbox"
                  className="ar-check"
                  checked={terms}
                  onChange={(e) => setTerms(e.target.checked)}
                  required
                />
                <label
                  htmlFor="terms-checkbox"
                  className="ar-check-label"
                  style={{ cursor: "pointer" }}
                >
                  I agree to&nbsp;
                  <a
                    href="https://techperformancecentre.com/terms-and-conditions"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Terms
                  </a>
                  &nbsp;and&nbsp;
                  <a
                    href="https://techperformancecentre.com/terms-and-conditions"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Privacy
                  </a>
                  .
                </label>
              </div>

              <div
                style={{
                  maxHeight: !terms ? "50px" : "0",
                  overflow: "hidden",
                  transition:
                    "max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), margin 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease",
                  marginBottom: !terms ? "10px" : "0",
                  opacity: !terms ? 1 : 0,
                }}
              >
                <p
                  style={{
                    color: "#f59e0b",
                    fontSize: "0.85rem",
                    textAlign: "center",
                    margin: "0",
                    fontWeight: "500",
                  }}
                >
                  ⚠️ Please accept Terms & Privacy to continue
                </p>
              </div>

              <button
                type="submit"
                className="ar-button"
                disabled={!terms || busy}
              >
                {busy ? (
                  <div className="ar-button-loader">
                    <div className="ar-spinner"></div>
                    <span>Creating account...</span>
                  </div>
                ) : (
                  "Create"
                )}
              </button>

              <div className="ar-divider">
                <span>or</span>
              </div>

              <button
                type="button"
                className="ar-button ar-google-button"
                onClick={handleGoogleSignIn}
                disabled={busy}
                style={{
                  opacity: busy ? 0.6 : 1,
                  cursor: busy ? "not-allowed" : "pointer",
                }}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"
                    fill="#4285F4"
                  />
                  <path
                    d="M9.003 18c2.43 0 4.467-.806 5.956-2.18L12.05 13.56c-.806.54-1.836.86-3.047.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9.003 18z"
                    fill="#34A853"
                  />
                  <path
                    d="M3.964 10.712c-.18-.54-.282-1.117-.282-1.71 0-.593.102-1.17.282-1.71V4.96H.957C.347 6.175 0 7.55 0 9.002c0 1.452.348 2.827.957 4.042l3.007-2.332z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M9.003 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.464.891 11.426 0 9.003 0 5.482 0 2.438 2.017.957 4.958L3.964 7.29c.708-2.127 2.692-3.71 5.036-3.71z"
                    fill="#EA4335"
                  />
                </svg>
                Sign in with Google
              </button>

              <p className="ar-small">
                Already have an account? &nbsp;
                <Link to={CONFIG.loginRoute}>Log in</Link>
              </p>
            </form>
          )}
        </div>
      </div>

      {/* Google Sign-In Loading Overlay - Outside of blurred container */}
      {googleSignInLoading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 99999,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "40px",
              borderRadius: "12px",
              boxShadow: "0 20px 40px rgba(0, 0, 0, 0.3)",
              textAlign: "center",
              maxWidth: "400px",
              width: "90%",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "20px",
              }}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                style={{
                  animation: "spin 1s linear infinite",
                  marginRight: "12px",
                }}
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="#4285F4"
                  strokeWidth="2"
                  fill="none"
                  strokeDasharray="60"
                  strokeDashoffset="15"
                />
              </svg>
              <h3
                style={{
                  margin: 0,
                  color: "#333",
                  fontSize: "18px",
                }}
              >
                Signing in with Google...
              </h3>
            </div>
            <p
              style={{
                margin: 0,
                color: "#666",
                fontSize: "14px",
                lineHeight: "1.5",
              }}
            >
              Please complete the Google sign-in process in the popup window.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
